import saveAs from 'file-saver';
import {
  getOidcAuthority,
  getOidcClientId,
  getOidcClientRoot,
  getOidcScope,
} from './env-vars';
import { User } from './models/UserModels';
import { ProductApiModel } from './models/ProductModel';
import { DealGroupApiModel } from './models/DealModel';

const getNavigatorLanguage = () => {
  if (navigator.languages && navigator.languages.length) {
    return navigator.languages[0];
  }
  return navigator.language;
};

export function getUserText(user: Pick<User, 'personName' | 'email'>) {
  const { personName, email } = user;
  if (personName && !email) {
    return `${personName}`;
  }
  if (!personName && email) {
    return `${email}`;
  }
  return `${personName} || ${email}`;
}

// TODO: refactor when business logic requires to set all PMI ID to default to hyphen instead of N/A
export function getTextForNoPersonId(user: User, isHyphenDefault?: boolean) {
  const defaultText = isHyphenDefault ? '-' : 'N/A';
  return user.hasAcceptedToShareData === false ? '0000000' : defaultText;
}

export function toLongLocaleString(dateStr: string) {
  if (!dateStr?.trim()) return null;
  const date = new Date(dateStr);
  try {
    return date.toLocaleString(getNavigatorLanguage(), {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (ex) {
    return dateStr;
  }
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function toDayMonthYear(dateStr: string) {
  if (!dateStr?.trim()) return null;
  const date = new Date(dateStr);
  try {
    return `${date.getDate() <= 9 ? `0${date.getDate()}` : date.getDate()} ${
      MONTHS[date.getMonth()]
    } ${date.getFullYear()}`;
  } catch (ex) {
    return dateStr;
  }
}

const MonthFullName = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function PMIFormatDate(dateStr: string) {
  if (!dateStr?.trim()) return null;
  const date = new Date(dateStr);
  try {
    return `${date.getDate() <= 9 ? `0${date.getDate()}` : date.getDate()} ${
      MonthFullName[date.getMonth()]
    }, ${date.getFullYear()}`;
  } catch (ex) {
    return dateStr;
  }
}

export function toShortLocaleDateString(dateStr: string) {
  if (!dateStr?.trim()) return null;

  const date = new Date(dateStr);

  try {
    return date.toLocaleString(getNavigatorLanguage(), {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    });
  } catch (ex) {
    return dateStr;
  }
}

export const toPMIFormatDateWithTime = (dateStr: string) => {
  if (!dateStr?.trim()) return null;
  try {
    const parsedDate = new Date(dateStr);
    // Create the custom format
    const dateTimeFormat = `${parsedDate
      .getDate()
      .toString()
      .padStart(2, '0')} ${
      MONTHS[parsedDate.getMonth()]
    }  ${parsedDate.getFullYear()} ${parsedDate
      .getHours()
      .toString()
      .padStart(2, '0')}:${parsedDate
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;

    return dateTimeFormat;
  } catch (e) {}
  return null;
};

export const getBrowserTimezone = () =>
  new Date()
    .toLocaleTimeString(undefined, { timeZoneName: 'short' })
    .split(' ')[2];

export const buildAdminInviteUrl = (inviteId: string) =>
  `${getOidcAuthority()}rbac/${inviteId}/accept?clientId=${getOidcClientId()}&responseType=code`;

export const buildAdmin2faUrl = (accountId: string, roleAssignmentId: string) =>
  `${getOidcAuthority()}rbac/${roleAssignmentId}?clientId=${getOidcClientId()}&responseType=code&returnUrl=${getOidcClientRoot()}/admin/account-2fa/${accountId}`;

export const buildAdmin2faUrlWithAgreement = (
  accountId: string,
  agreementId: string,
  roleAssignmentId: string,
) =>
  `${getOidcAuthority()}rbac/${roleAssignmentId}?clientId=${getOidcClientId()}&responseType=code&returnUrl=${getOidcClientRoot()}/admin/account-2fa/${accountId}/${agreementId}`;

export const buildTrainingHubUrl = (accountId: string) =>
  `${getOidcClientRoot()}/hub/${accountId}`;

export const buildVerifyAccessUrl = (accountId: string) =>
  `${getOidcAuthority()}b2b/verify?accountId=${accountId}&responseType=code&clientId=${getOidcClientId()}&returnUrl=${getOidcClientRoot()}/hub/${accountId}&scope=${encodeURIComponent(
    getOidcScope(),
  )}`;

export const verifyUserFlow = (accountId: string) => {
  const verifyAccessUrl = buildVerifyAccessUrl(accountId);
  window.location.href = verifyAccessUrl;
};

export const acceptUserinvitationFlow = (inviteId: string) => {
  const acceptInviteUrl = `${getOidcAuthority()}b2b/${inviteId}/accept?clientId=${getOidcClientId()}&responseType=code&scope=${encodeURIComponent(
    getOidcScope(),
  )}`;
  window.location.href = acceptInviteUrl;
};

export const normalizeEmail = (value?: string | null): string =>
  (value ?? '').trim();

// Multi-address pattern: matches one or more comma/semicolon/space separated emails.
export const emailRegex =
  /^[A-Za-z0-9][A-Za-z0-9._-]*@[A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,}(?:\s*[,;\s]\s*[A-Za-z0-9][A-Za-z0-9._-]*@[A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,})*\s*$/;
export const singleEmailRegex =
  /^[A-Za-z0-9][A-Za-z0-9._-]*@[A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,}$/; // eslint-disable-line
export const validDomainRegex =
  /^((((?!\\-))(xn\\-\\-)?[A-Za-z0-9\-_]{0,61}[A-Za-z0-9]{1,1}\.)*(xn\\-\\-)?([A-Za-z0-9\\-]{1,61}|[A-Za-z0-9\\-]{1,30})\.[A-Za-z]{2,}(([ ]{0,}))?(,|\s|;)?(([ ]{0,}))?)+$/;
export const pmiEmailRegex = /^\w+([-+.]\w+)*@(pm\.com|pmi\.org)$/; // eslint-disable-line

export const includes = (str: string = '', value: string = '') => {
  const collection = str.toLowerCase();
  return collection.includes(value.toLocaleLowerCase());
};

export function extractTextFromHtml(html: string) {
  if (!html) return null;

  const tempElement = document.createElement('div');
  tempElement.innerHTML = html;

  const text = tempElement.textContent || tempElement.innerText;
  return text.trim();
}

export const sanitize = (str: string) =>
  extractTextFromHtml(str)
    ?.replace(/[^\x00-\x7F]/g, '') // eslint-disable-line no-control-regex
    ?.replace(/\s+/g, ' ');

export const getItemsCountLabel = (
  count: number,
  label: string,
  txt?: string,
) => `${count} ${txt || ''} ${count === 1 ? label : `${label}s`}`;

export const getProductsCountLabel = (productsCount: number, txt?: string) =>
  getItemsCountLabel(productsCount, 'product', txt);
export const getAgreementsCountLabel = (productsCount: number, txt?: string) =>
  getItemsCountLabel(productsCount, 'agreement', txt);

export async function saveAsResponse(response: Response, fileName: string) {
  if (!fileName) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(
      response.headers.get('content-disposition'),
    );
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, ''); // eslint-disable-line no-param-reassign
    }
  }

  const blob = await response.blob();
  saveAs(blob, fileName);
}

export function extractDate(date: string) {
  return date.split('T')[0];
}

export function getSpecificMonthOfTheYear(monthDiff: number) {
  const currentDate = new Date();
  // dynamic monthDiff to get specific month
  const currentMonth = MonthFullName[currentDate.getMonth() + monthDiff];
  const currentYear = currentDate.getFullYear();
  return `${currentMonth} ${currentYear}`;
}

export function groupBy<T, K>(
  list: T[],
  keyGetter: (item: T) => K,
): Map<K, T[]> {
  const map = new Map<K, T[]>();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

export function getLinkModule() {
  return window.location.pathname.startsWith('/hub/')
    ? 'b2bhub_topnav'
    : 'b2badmin_topnav';
}

export function isAdminOrHub() {
  return (
    window.location.pathname.startsWith('/admin/') ||
    window.location.pathname.startsWith('/hub/')
  );
}

export function isProductRetiredOrLegacy(p: ProductApiModel) {
  return (
    (p.quantityRemaining === 0 && (p.sku === '101010' || p.sku === '101013')) ||
    p.retired
  );
}

const dealQueryTransformCache = new WeakMap<object, any>();

export const excludeLegacyAndRetiredProductsFromDealQuery = (result: any) => {
  if (!result.data) return result;
  if (dealQueryTransformCache.has(result.data)) {
    return { ...result, data: dealQueryTransformCache.get(result.data) };
  }

  const products = result.data.deal.products.filter(
    (p: ProductApiModel) => !isProductRetiredOrLegacy(p),
  );

  const groups = result.data.deal.groups.map((group: DealGroupApiModel) => ({
    ...group,
    products: group.products.filter(
      (p: ProductApiModel) => !isProductRetiredOrLegacy(p),
    ),
  }));

  const data = {
    ...result.data,
    deal: { ...result.data.deal, products, groups },
  };
  dealQueryTransformCache.set(result.data, data);
  return { ...result, data };
};

export interface CommonErrorResponse {
  status: number;
  data: {
    type: string;
    title: string;
    status: number;
    detail: string;
    traceId: string;
    ErrorCode: string;
    Value: string;
  };
}
export interface CommonErrorResponseDataValue {
  id: string;
  statusCode: number;
  error: string;
}

export const getResponseErrorMessage = (
  error: CommonErrorResponse,
): string | null => {
  if (error.data && error.data.Value) {
    try {
      const value: CommonErrorResponseDataValue | null = JSON.parse(
        error.data.Value,
      ) as CommonErrorResponseDataValue;
      return value.error ?? null;
    } catch (e) {}
  }
  return null;
};
